
import Vue from "vue";
import { Component, Prop, Watch } from "vue-property-decorator";
import { SpacingBarDescription } from "@/types/elements";
import { getSpacingPropertyValue } from "@/lib/canvas/spacing-bars";
import {
  ElementPositionAndSize,
  getDomElementFromTreeNode,
  getDomElementPositionAndSize
} from "@/lib/canvas/helpers";
import { uiStore } from "@/store/store-accessor";
import { forceNextTick } from "@/util/force-next-tick";

@Component({})
export default class SpacingBar extends Vue {
  @Prop()
  bar!: SpacingBarDescription;

  @Prop()
  isDragging!: boolean;

  @Prop()
  isActiveBar!: boolean;

  isHovering = false;

  resizeObserver: ResizeObserver = new ResizeObserver(
    this.onActiveElementResize
  );

  activeElementPositionAndSize: ElementPositionAndSize | null = null;
  valueAtMomentOfElementPositionAndSizeCalculation: number | null = null;

  intervalHandle: NodeJS.Timeout | undefined;

  get value() {
    return getSpacingPropertyValue(
      this.bar.affectedPropertyPath,
      this.bar.cssProperty
    );
  }

  get activeElement() {
    return uiStore.activeElement;
  }

  updateActiveElementPositionWhenItsPresent() {
    const activeElement = this.activeElement;
    if (activeElement) {
      const activeElementDomNode = getDomElementFromTreeNode(activeElement);
      if (activeElementDomNode) {
        this.activeElementPositionAndSize =
          getDomElementPositionAndSize(activeElementDomNode) || null;
        // Defensively copy this.value in case it'll become an object in the future
        this.valueAtMomentOfElementPositionAndSizeCalculation = this.value;

        return;
      }
    }

    this.activeElementPositionAndSize = null;
    this.valueAtMomentOfElementPositionAndSizeCalculation = null;
  }

  mounted() {
    this.intervalHandle = setInterval(() => {
      forceNextTick(this.updateActiveElementPositionWhenItsPresent);
    }, 50);
  }

  @Watch("activeElement", { immediate: true })
  handleActiveElementChange() {
    this.updateActiveElementPositionWhenItsPresent();

    this.resizeObserver.disconnect();

    const activeElement = this.activeElement;

    if (activeElement) {
      const activeElementDomNode = getDomElementFromTreeNode(activeElement);
      if (activeElementDomNode) {
        this.resizeObserver.observe(activeElementDomNode, {
          box: "border-box"
        });
        if (activeElementDomNode.parentElement) {
          this.resizeObserver.observe(activeElementDomNode.parentElement, {
            box: "border-box"
          });
        }
      }
    }
  }

  onActiveElementResize() {
    this.updateActiveElementPositionWhenItsPresent();
  }

  get classList() {
    return [`spacing-bar-${this.bar.location}`];
  }

  get style() {
    if (
      (this.isDragging && this.isActiveBar) ||
      // don't show other bars while dragging
      (this.isHovering && !this.isDragging)
    ) {
      return { ...this.barStyle, opacity: 1 };
    }

    return this.barStyle;
  }

  get barStyle() {
    const positionAndSize = this.activeElementPositionAndSize;

    if (!positionAndSize) return {};

    const valueAtMomentOfElementPositionAndSizeCalculation =
      this.valueAtMomentOfElementPositionAndSizeCalculation || this.value;

    const { width, height, y: top, x: left } = positionAndSize;

    const valueMin5 =
      valueAtMomentOfElementPositionAndSizeCalculation > 5
        ? valueAtMomentOfElementPositionAndSizeCalculation
        : 5;

    switch (this.bar.location) {
      case "inside-top":
        return {
          width: `${width}px`,
          height: `${valueMin5}px`,
          top: `${top}px`,
          left: `${left}px`,
          cursor: "row-resize"
        };
      case "outside-top":
        return {
          width: `${width}px`,
          height: `${valueMin5}px`,
          top: `${-valueMin5 + top}px`,
          left: `${left}px`,
          cursor: "row-resize"
        };
      case "inside-bottom":
        return {
          width: `${width}px`,
          height: `${valueMin5}px`,
          top: `${top + height - valueMin5}px`,
          left: `${left}px`,
          cursor: "row-resize"
        };
      case "outside-bottom":
        return {
          width: `${width}px`,
          height: `${valueMin5}px`,
          top: `${top + height}px`,
          left: `${left}px`,
          cursor: "row-resize"
        };
      case "inside-left":
        return {
          width: `${valueMin5}px`,
          top: `${top}px`,
          height: `${height}px`,
          left: `${left}px`,
          cursor: "col-resize"
        };
      case "outside-left":
        return {
          width: `${valueMin5}px`,
          top: `${top}px`,
          height: `${height}px`,
          left: `${left - valueMin5}px`,
          cursor: "col-resize"
        };
      case "inside-right":
        return {
          width: `${valueMin5}px`,
          top: `${top}px`,
          height: `${height}px`,
          left: `${left + width - valueMin5}px`,
          cursor: "col-resize"
        };
      case "outside-right":
        return {
          width: `${valueMin5}px`,
          top: `${top}px`,
          height: `${height}px`,
          left: `${left + width}px`,
          cursor: "col-resize"
        };
      default:
        return {};
    }
  }

  beforeDestroy() {
    this.resizeObserver.disconnect();
    if (this.intervalHandle) {
      clearInterval(this.intervalHandle);
    }
  }
}
